import React, { useCallback, useRef, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import { useSearchParams } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { Flex, Text, TextVariant, Button } from '@mprolab/uikit'
import MainWrapper from 'layouts/MainWrapper'
import { StyledBiennale } from 'components'
import {
  useGetPublicNodeStorageShareListQuery,
  useGetPublicNodeStorageShareSummaryQuery
} from 'state/services/indexNode/indexNode'
import { useTranslation } from 'react-i18next'
import useToast from 'hooks/useToast'
import { Folder, File } from 'react-feather'
import {
  buildPublicNodeStorageShareUrl,
  deletePublicShareItem,
  downloadPublicShareFile,
  mkdirPublicShareFolder,
  movePublicShareItem,
  uploadPublicShareFileChunked,
  uploadPublicShareSmallFile
} from 'utils/publicNodeStorageShare'

const StyledTitle = styled(StyledBiennale)`
  font-size: 28px;
`

const Shell = styled(Flex)`
  width: 100%;
  flex-direction: column;
  gap: 14px;
  padding: 24px;
  background: ${({ theme }) => theme.colors.backgroundContrast};
  border-radius: 12px;
  min-height: 280px;
`

const Toolbar = styled(Flex)`
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`

const Crumb = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.cyan};
  font-size: 13px;
  &:hover {
    text-decoration: underline;
  }
`

const RowGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(96px, 128px) 72px auto;
  gap: 10px;
  align-items: center;
  padding: 10px 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.background};
`

const ListHeaderGrid = styled(RowGrid)`
  opacity: 0.85;
  padding-top: 8px;
  padding-bottom: 8px;
`

const DropSurface = styled(Flex)<{ $active: boolean }>`
  flex-direction: column;
  gap: 12px;
  min-height: 200px;
  border-radius: 12px;
  border: 2px dashed
    ${({ theme, $active }) => ($active ? theme.colors.cyan : theme.colors.gray8)};
  background: ${({ theme, $active }) => ($active ? `${theme.colors.cyan}18` : 'transparent')};
  padding: 14px;
`

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`
}

function joinPath(base: string, segment: string): string {
  if (!base) return segment
  return `${base.replace(/\/$/, '')}/${segment}`
}

function sanitizeNewSegment(raw: string): string | null {
  const s = raw.trim()
  if (!s) return null
  if (s.includes('/') || s.includes('\\')) return null
  if (s === '.' || s === '..') return null
  if (s.startsWith('.')) return null
  return s
}

const SharedNodeStorage: React.FC = () => {
  const { t } = useTranslation()
  const { colors } = useTheme()
  const { toastError, toastSuccess } = useToast()
  const [params] = useSearchParams()
  const token = params.get('t')?.trim() ?? ''

  const [subFolder, setSubFolder] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dragDepthRef = useRef(0)
  const [isDragging, setIsDragging] = useState(false)

  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError
  } = useGetPublicNodeStorageShareSummaryQuery({ token }, { skip: !token })

  const {
    data: listing,
    isLoading: listLoading,
    refetch: refetchList
  } = useGetPublicNodeStorageShareListQuery(
    { token, path: subFolder || undefined },
    { skip: !token }
  )

  const canWrite = summary?.permission === 'read_write'
  const maxSingle = summary?.chunkUpload?.maxChunkBytes ?? 4_194_304

  const copyLink = useCallback(async () => {
    if (!token) return
    const url = buildPublicNodeStorageShareUrl(token)
    try {
      await navigator.clipboard.writeText(url)
      toastSuccess(t('nodesStorage.shared.linkCopied'))
    } catch {
      toastError(t('nodesStorage.shared.copyFailed'))
    }
  }, [token, toastError, toastSuccess, t])

  const uploadFiles = async (files: File[]) => {
    if (!token || !summary || !canWrite) return
    for (const file of files) {
      const relativePath = subFolder ? joinPath(subFolder, file.name) : file.name
      try {
        if (file.size <= maxSingle) {
          await uploadPublicShareSmallFile({ token, file, relativePath })
        } else {
          await uploadPublicShareFileChunked({ token, file, relativePath, summary })
        }
      } catch {
        toastError(t('nodesStorage.uploadFailed'))
        return
      }
    }
    toastSuccess(t('nodesStorage.uploadDone'))
    void refetchList()
  }

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files
    e.target.value = ''
    if (!list?.length) return
    await uploadFiles(Array.from(list))
  }

  const onDownload = async (rel: string, name: string) => {
    if (!token) return
    try {
      await downloadPublicShareFile({ token, path: rel, filenameHint: name })
    } catch {
      toastError(t('nodesStorage.downloadFailed'))
    }
  }

  const onDelete = async (rel: string) => {
    if (!token || !canWrite) return
    if (!window.confirm(t('nodesStorage.confirmDelete'))) return
    try {
      await deletePublicShareItem({ token, path: rel })
      toastSuccess(t('nodesStorage.deleteDone'))
      void refetchList()
    } catch {
      toastError(t('nodesStorage.deleteFailed'))
    }
  }

  const onNewFolder = async () => {
    if (!token || !canWrite) return
    const raw = window.prompt(t('nodesStorage.newFolderPrompt'))
    if (raw == null) return
    const seg = sanitizeNewSegment(raw)
    if (!seg) {
      toastError(t('nodesStorage.invalidName'))
      return
    }
    const target = subFolder ? joinPath(subFolder, seg) : seg
    try {
      await mkdirPublicShareFolder({ token, path: target })
      toastSuccess(t('nodesStorage.folderCreated'))
      void refetchList()
    } catch {
      toastError(t('nodesStorage.mkdirFailed'))
    }
  }

  const onRename = async (entName: string) => {
    if (!token || !canWrite) return
    const fromRel = subFolder ? joinPath(subFolder, entName) : entName
    const raw = window.prompt(t('nodesStorage.renamePrompt'), entName)
    if (raw == null) return
    const seg = sanitizeNewSegment(raw)
    if (!seg) {
      toastError(t('nodesStorage.invalidName'))
      return
    }
    const toRel = subFolder ? joinPath(subFolder, seg) : seg
    if (fromRel === toRel) return
    try {
      await movePublicShareItem({ token, from: fromRel, to: toRel })
      toastSuccess(t('nodesStorage.renameDone'))
      void refetchList()
    } catch {
      toastError(t('nodesStorage.renameFailed'))
    }
  }

  const onDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!canWrite) return
    dragDepthRef.current += 1
    setIsDragging(true)
  }

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const onDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragDepthRef.current -= 1
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0
      setIsDragging(false)
    }
  }

  const onDrop = async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragDepthRef.current = 0
    setIsDragging(false)
    if (!canWrite || !summary) return
    const fl = e.dataTransfer.files
    if (!fl?.length) return
    await uploadFiles(Array.from(fl))
  }

  const breadcrumbParts = subFolder ? subFolder.split('/').filter(Boolean) : []

  const invalidToken =
    summaryError &&
    typeof summaryError === 'object' &&
    'status' in summaryError &&
    (summaryError.status === 401 || summaryError.status === 403)

  return (
    <MainWrapper>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Flex fullWidth mt="80px" mb="120px" flexDirection="column">
        <Flex mt="72px" mb="16px" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap="16px">
          <StyledTitle>{t('nodesStorage.shared.title')}</StyledTitle>
          {token ? (
            <Button type="button" variant="secondary" size="sm" onClick={() => void copyLink()}>
              {t('nodesStorage.shared.copyLink')}
            </Button>
          ) : null}
        </Flex>
        <Text variant={TextVariant.BODY_DEFAULT} color={colors.gray6} mb="16px" maxWidth="720px">
          {t('nodesStorage.shared.subtitle')}
        </Text>

        {!token ? (
          <Text variant={TextVariant.BODY_DEFAULT} color={colors.magenta}>
            {t('nodesStorage.shared.missingToken')}
          </Text>
        ) : invalidToken ? (
          <Text variant={TextVariant.BODY_DEFAULT} color={colors.magenta}>
            {t('nodesStorage.shared.invalidOrRevoked')}
          </Text>
        ) : summaryLoading ? (
          <Text variant={TextVariant.BODY_DEFAULT}>{t('nodesStorage.loadingQuota')}</Text>
        ) : summary ? (
          <Shell>
            <Flex gap="12px" alignItems="center" flexWrap="wrap">
              <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
                {t('nodesStorage.shared.permissionLabel')}:{' '}
                <Text as="span" variant={TextVariant.BODY_SMALL_BOLD} color={colors.white}>
                  {summary.permission === 'read_write'
                    ? t('nodesStorage.shared.permissionReadWrite')
                    : t('nodesStorage.shared.permissionRead')}
                </Text>
              </Text>
              {summary.folderPath ? (
                <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray8}>
                  {t('nodesStorage.shared.sharedRoot', { path: summary.folderPath })}
                </Text>
              ) : (
                <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray8}>
                  {t('nodesStorage.shared.sharedRootAll')}
                </Text>
              )}
            </Flex>

            <DropSurface
              $active={isDragging && canWrite}
              onDragEnter={onDragEnter}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={(e) => void onDrop(e)}
            >
              {isDragging && canWrite ? (
                <Text variant={TextVariant.BODY_DEFAULT_BOLD} color={colors.cyan} textAlign="center">
                  {t('nodesStorage.dropFilesHere')}
                </Text>
              ) : null}
              <Toolbar>
                <Flex gap="8px" alignItems="center" flexWrap="wrap" flex="1" minWidth="0">
                  <Crumb type="button" onClick={() => setSubFolder('')}>
                    {t('nodesStorage.allFiles')}
                  </Crumb>
                  {breadcrumbParts.map((seg, i) => {
                    const pathUpTo = breadcrumbParts.slice(0, i + 1).join('/')
                    return (
                      <Flex key={pathUpTo} gap="6px" alignItems="center">
                        <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                          /
                        </Text>
                        <Crumb type="button" onClick={() => setSubFolder(pathUpTo)}>
                          {seg}
                        </Crumb>
                      </Flex>
                    )
                  })}
                </Flex>
                {canWrite ? (
                  <>
                    <Button type="button" variant="secondary" size="sm" onClick={() => void onNewFolder()}>
                      {t('nodesStorage.newFolder')}
                    </Button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      hidden
                      onChange={onPickFile}
                    />
                    <Button type="button" variant="primary" size="sm" onClick={() => fileInputRef.current?.click()}>
                      {t('nodesStorage.upload')}
                    </Button>
                  </>
                ) : null}
              </Toolbar>

              {listLoading ? (
                <Text variant={TextVariant.BODY_DEFAULT}>{t('nodesStorage.loadingFiles')}</Text>
              ) : (
                <Flex flexDirection="column" gap="6px">
                  <ListHeaderGrid>
                    <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                      {t('nodesStorage.name')}
                    </Text>
                    <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                      {t('nodesStorage.modified')}
                    </Text>
                    <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                      {t('nodesStorage.size')}
                    </Text>
                    <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6} style={{ textAlign: 'right' }}>
                      {t('nodesStorage.actions')}
                    </Text>
                  </ListHeaderGrid>
                  {(listing?.entries ?? [])
                    .slice()
                    .sort((a, b) => {
                      if (a.isDirectory !== b.isDirectory) return a.isDirectory ? -1 : 1
                      return a.name.localeCompare(b.name)
                    })
                    .map((ent) => {
                      const rel = subFolder ? joinPath(subFolder, ent.name) : ent.name
                      return (
                        <RowGrid key={rel}>
                          <Flex
                            gap="10px"
                            alignItems="center"
                            minWidth={0}
                            style={{ cursor: ent.isDirectory ? 'pointer' : undefined }}
                            onClick={() => ent.isDirectory && setSubFolder(rel)}
                          >
                            {ent.isDirectory ? (
                              <Folder size={18} color={colors.gray6} />
                            ) : (
                              <File size={18} color={colors.gray6} />
                            )}
                            <Text
                              variant={TextVariant.BODY_DEFAULT_BOLD}
                              style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {ent.name}
                            </Text>
                          </Flex>
                          <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                            {new Date(ent.mtimeMs).toLocaleString()}
                          </Text>
                          <Text variant={TextVariant.TEXT_XS_REGULAR} color={colors.gray6}>
                            {!ent.isDirectory && ent.size != null ? formatBytes(ent.size) : '—'}
                          </Text>
                          <Flex gap="6px" justifyContent="flex-end" flexWrap="wrap">
                            {ent.isDirectory ? (
                              <Button variant="secondary" size="sm" onClick={() => setSubFolder(rel)}>
                                {t('general.details')}
                              </Button>
                            ) : (
                              <Button variant="secondary" size="sm" onClick={() => void onDownload(rel, ent.name)}>
                                {t('general.download')}
                              </Button>
                            )}
                            {canWrite ? (
                              <>
                                <Button variant="secondary" size="sm" onClick={() => void onRename(ent.name)}>
                                  {t('nodesStorage.rename')}
                                </Button>
                                <Button variant="secondary" size="sm" onClick={() => void onDelete(rel)}>
                                  {t('general.delete')}
                                </Button>
                              </>
                            ) : null}
                          </Flex>
                        </RowGrid>
                      )
                    })}
                  {(listing?.entries?.length ?? 0) === 0 ? (
                    <Text variant={TextVariant.BODY_SMALL} color={colors.gray6}>
                      {t('nodesStorage.emptyFolder')}
                    </Text>
                  ) : null}
                </Flex>
              )}
            </DropSurface>
          </Shell>
        ) : null}
      </Flex>
    </MainWrapper>
  )
}

export default SharedNodeStorage
